import { useEffect, useState } from 'react';

import {
  FlightGuestBoxProps,
  FlightGuestProps,
} from '@components/views/flight/types';
import useGuestBoxErrorMessage from '@components/Guest/GuestBox/useGuestBoxErrorMessage';
import {
  ADULT_DEFAULT_NUMBER,
  ADULT_MAX_NUMBER,
  CHILD_DEFAULT_NUMBER,
  CHILD_MAX_NUMBER,
  getTotalGuestNumberErrorMessage,
  getGuestMaxNumberErrorMessage,
  INFANT_DEFAULT_NUMBER,
  INFANT_MAX_NUMBER,
  MAX_PASSENGER_NUMBER,
} from '@components/views/flight/constants';

type FlightGuest = {
  numberOfAdultGuests: number;
  numberOfChildGuests: number;
  numberOfInfantGuests: number;
  removeAdult: () => void;
  addAdult: () => void;
  removeChild: () => void;
  addChild: () => void;
  removeInfant: () => void;
  addInfant: () => void;
  reset: () => void;
  errorMessages: string[];
};
const useFlightGuests = (
  props: Pick<
    FlightGuestBoxProps,
    'numberOfAdultGuests' | 'numberOfChildGuests' | 'numberOfInfantGuests'
  >,
  onApplyGuestsForm?: (params: FlightGuestProps) => void
): FlightGuest => {
  const [numberOfAdultGuests, setNumberOfAdultGuests] =
    useState<number>(ADULT_DEFAULT_NUMBER);
  const [numberOfChildGuests, setNumberOfChildGuests] =
    useState<number>(CHILD_DEFAULT_NUMBER);
  const [numberOfInfantGuests, setNumberOfInfantGuests] = useState<number>(
    INFANT_DEFAULT_NUMBER
  );
  const { addErrorMessage, clearMessages, lastErrorMessage } =
    useGuestBoxErrorMessage();

  useEffect(() => {
    setNumberOfAdultGuests(props.numberOfAdultGuests);
  }, [props.numberOfAdultGuests]);

  useEffect(() => {
    setNumberOfChildGuests(props.numberOfChildGuests);
  }, [props.numberOfChildGuests]);

  useEffect(() => {
    setNumberOfInfantGuests(props.numberOfInfantGuests);
  }, [props.numberOfInfantGuests]);

  useEffect(() => {
    clearMessages();
  }, [numberOfAdultGuests, numberOfChildGuests, numberOfInfantGuests]);

  const removeAdult = () => {
    const newAdultCount = numberOfAdultGuests - 1;
    onApplyGuestsForm?.({
      numberOfAdultGuests: newAdultCount,
      numberOfChildGuests,
      numberOfInfantGuests,
    });
    setNumberOfAdultGuests(newAdultCount);
  };

  const addAdult = () => {
    if (numberOfAdultGuests + numberOfChildGuests >= MAX_PASSENGER_NUMBER) {
      addErrorMessage(getTotalGuestNumberErrorMessage());
    } else if (numberOfAdultGuests >= ADULT_MAX_NUMBER) {
      addErrorMessage(
        getGuestMaxNumberErrorMessage('Yetişkin', ADULT_MAX_NUMBER)
      );
    } else {
      const newAdultCount = numberOfAdultGuests + 1;
      onApplyGuestsForm?.({
        numberOfAdultGuests: newAdultCount,
        numberOfChildGuests,
        numberOfInfantGuests,
      });
      setNumberOfAdultGuests(newAdultCount);
    }
  };

  const removeChild = () => {
    const newChildCount = numberOfChildGuests - 1;
    onApplyGuestsForm?.({
      numberOfAdultGuests,
      numberOfChildGuests: newChildCount,
      numberOfInfantGuests,
    });
    setNumberOfChildGuests(newChildCount);
  };

  const addChild = () => {
    if (numberOfAdultGuests + numberOfChildGuests >= MAX_PASSENGER_NUMBER) {
      addErrorMessage(getTotalGuestNumberErrorMessage());
    } else if (numberOfChildGuests >= CHILD_MAX_NUMBER) {
      addErrorMessage(getGuestMaxNumberErrorMessage('Çocuk', CHILD_MAX_NUMBER));
    } else {
      const newChildCount = numberOfChildGuests + 1;
      onApplyGuestsForm?.({
        numberOfAdultGuests,
        numberOfChildGuests: newChildCount,
        numberOfInfantGuests,
      });
      setNumberOfChildGuests(newChildCount);
    }
  };

  const removeInfant = () => {
    const newInfantCount = numberOfInfantGuests - 1;
    onApplyGuestsForm?.({
      numberOfAdultGuests,
      numberOfChildGuests,
      numberOfInfantGuests: newInfantCount,
    });
    setNumberOfInfantGuests(newInfantCount);
  };

  const addInfant = () => {
    if (numberOfInfantGuests >= numberOfAdultGuests) {
      addErrorMessage(
        'Bebek yolcu sayısı, yetişkin yolcu sayısından fazla olamaz.'
      );
    } else if (numberOfInfantGuests >= INFANT_MAX_NUMBER) {
      addErrorMessage(
        getGuestMaxNumberErrorMessage('Bebek', INFANT_MAX_NUMBER)
      );
    } else {
      const newInfantCount = numberOfInfantGuests + 1;
      onApplyGuestsForm?.({
        numberOfAdultGuests,
        numberOfChildGuests,
        numberOfInfantGuests: newInfantCount,
      });
      setNumberOfInfantGuests(newInfantCount);
    }
  };

  const reset = () => {
    setNumberOfAdultGuests(props.numberOfAdultGuests);
    setNumberOfChildGuests(props.numberOfChildGuests);
    setNumberOfInfantGuests(props.numberOfInfantGuests);
  };

  return {
    numberOfAdultGuests,
    numberOfChildGuests,
    numberOfInfantGuests,
    removeAdult,
    addAdult,
    removeChild,
    addChild,
    removeInfant,
    addInfant,
    errorMessages: lastErrorMessage,
    reset,
  };
};

export default useFlightGuests;
